/**
 * Backend create appends questionnaire answers after this separator.
 * Keep in sync with Job.saveQuestionnaire / description sync on update.
 */
export const QUESTIONNAIRE_DESCRIPTION_SEPARATOR = '\n\n * * * \n\n';

/** Removes the questionnaire Q&A block from a job description, if present. */
export const stripQuestionnaireDescriptionAddendum = (
  description?: string | null,
): string => {
  if (!description) return '';
  const separatorIndex = description.indexOf(
    QUESTIONNAIRE_DESCRIPTION_SEPARATOR,
  );
  if (separatorIndex < 0) {
    return description;
  }
  return description.slice(0, separatorIndex).trimEnd();
};

/**
 * Builds the Q&A addendum text (without the separator), ordered by question priority.
 */
export const buildQuestionnaireDescriptionAddendum = (
  questions: Array<{
    questionTitle?: string | null;
    priority?: number | null;
  } | null>,
  customAttributes?: Array<{
    ref?: string | null;
    value?: string | null;
  } | null> | null,
): string => {
  if (!questions?.length || !customAttributes?.length) {
    return '';
  }

  const sortedQuestions = [...questions]
    .filter(
      (
        question,
      ): question is { questionTitle: string; priority?: number | null } =>
        !!question?.questionTitle,
    )
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

  let addendum = '';
  for (const question of sortedQuestions) {
    const attribute = customAttributes.find(
      (entry) => entry?.ref === question.questionTitle,
    );
    if (!attribute?.value) continue;
    addendum += `${question.questionTitle}: ${attribute.value}\n\n`;
  }
  return addendum;
};

/** Combines a base description with a questionnaire addendum when present. */
export const withQuestionnaireDescriptionAddendum = (
  baseDescription: string,
  addendum: string,
): string => {
  const base = stripQuestionnaireDescriptionAddendum(baseDescription).trimEnd();
  if (!addendum) {
    return base;
  }
  return `${base}${QUESTIONNAIRE_DESCRIPTION_SEPARATOR}${addendum}`;
};
